import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { AlbumDetail, PhotoCard, StudioPhotoDetail } from '@photos/shared';
import { useAlbumAction, useStudioPhoto, useUpdatePhotos } from '../../api/hooks';
import { AlbumPickerDialog } from '../../components/AlbumPicker';
import { ShareDialog } from '../../components/dialogs/ShareDialog';
import { VisibilityDialog } from '../../components/dialogs/VisibilityDialog';
import { Icon } from '../../components/Icon';
import { LightboxFrame } from '../../components/LightboxFrame';
import { fromFlock } from '../../components/PhotoGrid';
import { TagEditor } from '../../components/TagEditor';
import { useDownloads } from '../../hooks/useDownloads';
import { fmtLongDateTime, fmtMB, fmtShortDate, wallClock } from '../../lib/format';
import { VISIBILITY_LOOK } from '../../lib/visibility';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toasts';
import { knownAlbums } from './StudioActions';
import { ReleaseDialog, useUndoToast } from './studioUi';

interface StudioLightboxProps {
  photos: PhotoCard[];
  total?: number;
  index: number;
  /** the album page it was opened from */
  album?: AlbumDetail;
  onIndex(i: number): void;
  onClose(): void;
  onNearEnd?(): void;
}

type Dialog = 'share' | 'visibility' | 'release' | 'move' | 'add' | null;

/** OpenStreetMap link for a GPS position. */
export const mapUrl = (lat: number, lon: number) => `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=17/${lat}/${lon}`;

/** The viewer's Details: uploader, camera, size, file, GPS location (media only), copy origin, storage key. */
export function PhotoDetails({ p, d }: { p: PhotoCard; d: StudioPhotoDetail | undefined }) {
  return (
    <div className="sec">
      <div className="lbl">Details</div>
      <dl className="kv">
        <dt>Uploaded by</dt>
        <dd>
          {p.uploaderName} · {fmtShortDate(new Date(p.uploadedAt))}
        </dd>
        <dt>Camera</dt>
        <dd>{d?.camera || '—'}</dd>
        <dt>Dimensions</dt>
        <dd>
          {p.width} × {p.height}
        </dd>
        <dt>File</dt>
        <dd>
          {p.fileName} · {fmtMB(p.bytes)} MB
        </dd>
        {d?.location && (
          <>
            <dt>Location</dt>
            <dd>
              {d.location.lat.toFixed(4)}, {d.location.lon.toFixed(4)} ·{' '}
              <a href={mapUrl(d.location.lat, d.location.lon)} target="_blank" rel="noreferrer">
                Open map
              </a>
            </dd>
          </>
        )}
        {d?.copiedFrom && (
          <>
            <dt>Copy</dt>
            <dd>A separate copy of another photo</dd>
          </>
        )}
        {d?.originalKey && (
          <>
            <dt>Stored at</dt>
            <dd style={{ fontFamily: 'ui-monospace,monospace', fontSize: 11.5, wordBreak: 'break-all' }}>{d.originalKey}</dd>
          </>
        )}
      </dl>
    </div>
  );
}

/** Studio lightbox: full editing — tags, ★ Pick, visibility, album membership, Trash. */
export function StudioLightbox({ photos, total, index, album, onIndex, onClose, onNearEnd }: StudioLightboxProps) {
  const p = photos[index];
  const { access, tagIndex } = useSession();
  const detail = useStudioPhoto(p?.id);
  const update = useUpdatePhotos();
  const albumAction = useAlbumAction();
  const toast = useToast();
  const undoToast = useUndoToast();
  const { downloadOne } = useDownloads();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [tags, setTags] = useState<string[]>(p?.tags ?? []);

  useEffect(() => setTags(detail.data?.tags ?? p?.tags ?? []), [p?.id, p?.tags, detail.data?.tags]);

  // The frame closes itself when the photo is gone (e.g. trashed from here).
  if (!p) return <LightboxFrame photos={photos} index={index} title="" onIndex={onIndex} onClose={onClose}>{null}</LightboxFrame>;
  const d = detail.data;
  const albums = d?.albums.map((a) => a.id) ?? knownAlbums(p, album);
  const canTag = access.canTagIn(albums);
  const look = VISIBILITY_LOOK[p.visibility];
  const title = tagIndex.eventLabel(p.tags) || p.fileName;
  const fail = (e: Error) => toast({ icon: 'info', title: 'That didn’t work', sub: e.message });

  const saveTags = (next: string[]) => {
    const prev = tags;
    setTags(next);
    update.mutate(
      { ids: [p.id], albumId: album?.id, addTags: next.filter((t) => !prev.includes(t)), removeTags: prev.filter((t) => !next.includes(t)) },
      {
        onError: (e) => {
          setTags(prev);
          toast({ icon: 'info', title: 'Couldn’t save tags', sub: e.message });
        },
      },
    );
  };

  const togglePick = () => update.mutate({ ids: [p.id], albumId: album?.id, picked: !p.picked }, { onError: fail });

  const trash = () =>
    update.mutate(
      { ids: [p.id], albumId: album?.id, trash: true },
      {
        onSuccess: (r) => {
          undoToast({ icon: 'trash', title: 'Moved 1 photo to Trash' }, r.activityIds[0]);
          if (photos.length <= 1) onClose();
        },
        onError: fail,
      },
    );

  const albumChange = (action: 'move' | 'add' | 'copy' | 'remove', target: { id: string; name: string }, applyTagChanges = false) =>
    albumAction.mutate(
      { action, albumId: target.id, ids: [p.id], applyTagChanges },
      {
        onSuccess: (r) => {
          setDialog(null);
          const verb = { move: 'Moved 1 photo to', add: 'Added 1 photo to', copy: 'Copied 1 photo to', remove: 'Removed 1 photo from' }[action];
          undoToast({ icon: 'folder', title: `${verb} ${target.name}` }, r.activityId);
        },
        onError: fail,
      },
    );

  return (
    <LightboxFrame photos={photos} total={total} index={index} title={title} onIndex={onIndex} onClose={onClose} onNearEnd={onNearEnd} paused={!!dialog}>
      <h3>{title}</h3>
      <div className="sub">{fmtLongDateTime(wallClock(p.takenAt))}</div>
      <div className="sec" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <span className="pill" style={{ color: look.color }} title={look.description}>
          <Icon name={look.icon} />
          {look.label}
        </span>
        {p.picked && (
          <span className="pill" style={{ color: 'var(--gold)' }}>
            <Icon name="star" />
            Pick
          </span>
        )}
        {fromFlock(p) && (
          <span className="pill" style={{ color: '#b2620f' }}>
            <Icon name="flock" />
            Added from Flock
          </span>
        )}
      </div>
      <div className="sec">
        <div className="lbl">Albums</div>
        <div className="albumlist">
          {(d?.albums ?? []).map((a) => (
            <div key={a.id} className="albumrow">
              <Icon name="folder" />
              <Link to={`/studio/albums/${a.id}`} onClick={onClose}>
                {a.name}
              </Link>
              {a.home ? (
                <span className="pill">Home</span>
              ) : (
                access.isMedia && (
                  <button type="button" className="btn ghost sm" aria-label={`Remove from ${a.name}`} title="Remove from this album" onClick={() => albumChange('remove', a)}>
                    <Icon name="x" />
                  </button>
                )
              )}
            </div>
          ))}
        </div>
        {access.isMedia && (
          <div className="row2" style={{ marginTop: 8 }}>
            <button type="button" className="btn" onClick={() => setDialog('move')}>
              <Icon name="move" />
              Move…
            </button>
            <button type="button" className="btn" onClick={() => setDialog('add')}>
              <Icon name="folder" />
              Add to album…
            </button>
          </div>
        )}
      </div>
      <div className="sec">
        <div className="lbl">Download</div>
        <div className="acts">
          <button type="button" className="btn primary" onClick={() => downloadOne(p.id, 'original')}>
            <Icon name="dl" />
            Original · {fmtMB(p.bytes)} MB
          </button>
          <div className="row2">
            <button type="button" className="btn" onClick={() => downloadOne(p.id, 'large')}>
              Large 2048
            </button>
            <button type="button" className="btn" onClick={() => downloadOne(p.id, 'social')}>
              Social 1080
            </button>
          </div>
        </div>
      </div>
      <div className="sec">
        <div className="lbl">Tags</div>
        {canTag ? (
          <TagEditor className="tagbox" id="lbTags" tags={tags} onChange={saveTags} />
        ) : (
          <div className="tagbox" id="lbTags">
            {tags.map((t) => (
              <span key={t} className={`chip cat-${tagIndex.catOf(t)}`}>
                #{t}
              </span>
            ))}
          </div>
        )}
      </div>
      <PhotoDetails p={p} d={d} />
      <div className="sec">
        <div className="lbl">More</div>
        <div className="acts">
          {access.canRelease && p.visibility === 'unreleased' && (
            <button type="button" className="btn primary" onClick={() => setDialog('release')}>
              <Icon name="globe" />
              Release
            </button>
          )}
          {(canTag || access.canRelease || access.canRestrict) && (
            <div className="row2">
              {canTag && (
                <button type="button" className="btn" onClick={togglePick} disabled={update.isPending}>
                  <Icon name="star" />
                  {p.picked ? 'Unpick' : 'Pick'}
                </button>
              )}
              {(access.canRelease || access.canRestrict) && (
                <button type="button" className="btn" onClick={() => setDialog('visibility')}>
                  <Icon name="eye" />
                  Visibility
                </button>
              )}
            </div>
          )}
          {access.canShare(p) && (
            <button type="button" className="btn" onClick={() => setDialog('share')}>
              <Icon name="share" />
              Share link
            </button>
          )}
          {access.isMedia && (
            <button type="button" className="btn danger" onClick={trash} disabled={update.isPending}>
              <Icon name="trash" />
              Move to Trash
            </button>
          )}
        </div>
      </div>
      {dialog === 'share' && <ShareDialog photos={[p]} onClose={() => setDialog(null)} />}
      {dialog === 'visibility' && <VisibilityDialog photos={[p]} onClose={() => setDialog(null)} onDone={() => undefined} />}
      {dialog === 'release' && <ReleaseDialog target={{ ids: [p.id], albumId: album?.id }} onClose={() => setDialog(null)} />}
      {(dialog === 'move' || dialog === 'add') && (
        <AlbumPickerDialog
          title={dialog === 'move' ? 'Move to another album' : 'Add to an album'}
          help={dialog === 'move' ? 'Changes the album this photo lives in.' : 'Choose an album, then whether the photo shows in both albums or gets a separate copy.'}
          confirmLabel={dialog === 'move' ? 'Move' : 'Add'}
          excludeIds={d?.albums.filter((a) => dialog === 'add' || a.home).map((a) => a.id) ?? []}
          allowCreate={access.isMedia}
          busy={albumAction.isPending}
          tags={{ mode: dialog, photos: [p] }}
          copyChoice={dialog === 'add'}
          onPick={(target, apply, asCopy) => albumChange(asCopy ? 'copy' : dialog, target, apply)}
          onClose={() => setDialog(null)}
        />
      )}
    </LightboxFrame>
  );
}
